/**
 * Email Verification Route
 * Handles both the verification token from the email link and the manual verification page
 */

import React, { useState, useEffect } from 'react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { EmailVerificationPage } from '../pages/Auth/EmailVerificationPage'
import { requireGuest } from '../router/guards'
import { authService } from '../services/auth.service'

interface SearchParams {
  email?: string
  token?: string
}

const VerifyEmailRouteComponent = () => {
  const navigate = useNavigate()
  const search = useSearch({ from: '/verify-email' }) as SearchParams
  const token = search.token

  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying')
  const [errorMessage, setErrorMessage] = useState('')
  const verificationStarted = React.useRef(false)

  useEffect(() => {
    if (token && !verificationStarted.current) {
      verificationStarted.current = true
      handleTokenVerify(token)
    } else if (!token) {
      setStatus('error')
      setErrorMessage('No verification token found in URL.')
    }
  }, [token])

  const handleTokenVerify = async (token: string) => {
    try {
      await authService.verifyEmail(token)
      setStatus('success')
      // Redirect to login after success
      setTimeout(() => {
        navigate({ to: '/login', search: { verified: 'true' } })
      }, 2000)
    } catch (error: any) {
      console.error('Email verification failed:', error)
      setStatus('error')
      setErrorMessage(error?.response?.data?.detail || 'Invalid or expired verification link.')
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 text-center">
        {status === 'verifying' && (
          <>
            <div className="mb-6 mx-auto w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying your email</h1>
            <p className="text-gray-600">Please wait while we confirm your account...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6 mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
            <p className="text-gray-600">Your account is now active. Redirecting you to login...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mb-6 mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-red-600">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
            <p className="text-red-500 mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate({ to: '/login' })}
              className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors">
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/verify-email')({
  beforeLoad: requireGuest,
  component: VerifyEmailRouteComponent,
})
